<template>
  <div class="space-y-2" data-test="cover-letter-preferences">
    <div class="flex items-center justify-between">
      <span class="block text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Preferences</span>
      <span class="text-[10px] text-green-600 dark:text-green-400" aria-live="polite">{{ status }}</span>
    </div>
    <p class="text-[11px] text-muted-foreground">
      Every letter follows these. The chat updates them when you say things like "always one paragraph".
    </p>

    <p v-if="loadError" class="text-[11px] text-destructive">{{ loadError }}</p>

    <div v-for="group in groups" v-show="group.name === 'main' || showMore" :key="group.name" class="space-y-2.5">
      <div v-for="category in group.items" :key="category.key" :data-test="`pref-${category.key}`">
        <span class="block text-[11px] font-semibold text-foreground mb-1">{{ category.label }}</span>

        <div v-if="category.type === 'single' || category.type === 'multi'" class="flex flex-wrap gap-1">
          <button
            v-for="option in category.options"
            :key="option.value"
            type="button"
            :aria-pressed="isSelected(category, option.value)"
            :class="isSelected(category, option.value)
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-background text-muted-foreground border-border hover:border-primary hover:text-foreground'"
            class="px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors"
            @click="choose(category, option.value)"
          >
            {{ option.label }}
          </button>
        </div>

        <textarea
          v-else-if="category.type === 'textarea'"
          v-model="prefs[category.key]"
          :placeholder="category.placeholder"
          :maxlength="category.max_chars || undefined"
          rows="3"
          class="w-full border border-border rounded-lg px-2.5 py-1.5 text-xs bg-background text-foreground focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none resize-y"
          @change="save(category.key, (prefs[category.key] || '').trim())"
        ></textarea>

        <input
          v-else
          v-model="prefs[category.key]"
          type="text"
          :placeholder="category.placeholder"
          :maxlength="category.max_chars || undefined"
          class="w-full border border-border rounded-lg px-2.5 py-1.5 text-xs bg-background text-foreground focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
          @change="save(category.key, (prefs[category.key] || '').trim())"
        />

        <p v-if="category.help" class="text-[10px] text-muted-foreground mt-1">{{ category.help }}</p>
      </div>
    </div>

    <button
      v-if="categories.length"
      type="button"
      class="text-[11px] font-semibold text-primary hover:underline"
      :aria-expanded="showMore"
      data-test="prefs-more"
      @click="showMore = !showMore"
    >
      {{ showMore ? "Fewer options" : "More options" }}
    </button>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import coverLetterPreferences from "@/services/coverLetterPreferences";

export default {
  name: "CoverLetterPreferences",
  setup() {
    const categories = ref([]);
    const prefs = ref({});
    const showMore = ref(false);
    const status = ref("");
    const loadError = ref("");
    let statusTimer = null;

    const groups = computed(() => [
      { name: "main", items: categories.value.filter((c) => c.group === "main") },
      { name: "more", items: categories.value.filter((c) => c.group !== "main") },
    ]);

    const load = async () => {
      try {
        const data = await coverLetterPreferences.get();
        categories.value = data.categories || [];
        prefs.value = data.preferences || {};
        loadError.value = "";
      } catch (e) {
        loadError.value = "Could not load your preferences.";
      }
    };

    const save = async (key, value) => {
      prefs.value = { ...prefs.value, [key]: value };
      status.value = "Saving…";
      try {
        prefs.value = await coverLetterPreferences.save({ [key]: value });
        status.value = "Saved";
      } catch (e) {
        status.value = "Could not save";
      }
      clearTimeout(statusTimer);
      statusTimer = setTimeout(() => {
        status.value = "";
      }, 2000);
    };

    const isSelected = (category, value) =>
      category.type === "multi"
        ? (prefs.value[category.key] || []).includes(value)
        : prefs.value[category.key] === value;

    const choose = (category, value) => {
      if (category.type !== "multi") return save(category.key, value);
      const current = prefs.value[category.key] || [];
      return save(
        category.key,
        current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
      );
    };

    onMounted(load);

    return { categories, prefs, showMore, status, loadError, groups, save, isSelected, choose, load };
  },
};
</script>
